import { apiService } from './api';

const BASE_PATH = '/supplier/settings';

let settingsCache = null;
let cacheTimestamp = 0;
const CACHE_TTL = 30000;

function normalizeToCanonical(settings) {
  if (settings.business_profile) return settings;

  const ei = settings.editableInfo || {};
  const pd = settings.profileData || {};
  const bank = settings.bankingDetails || settings.banking || {};
  const docs = settings.documents || {};
  const fd = settings.formData || {};

  const legal_name = ei.legalName || fd.legalName || pd.legalName || '';
  const business_name = ei.publicName || fd.companyName || pd.publicName || '';
  const short_bio = ei.shortBio || fd.description || '';
  const emergency_contact = ei.emergencyContact || fd.emergencyContact || '';
  const individual_name = ei.individualName || fd.individualName || '';
  const phone = ei.contactValue || fd.contactInfo || pd.contactValue || '';
  const business_type = ei.accountType || fd.legalStatus || pd.accountType || '';
  const registration_number = ei.regNumber || fd.businessLicenseNumber || '';

  return {
    business_profile: {
      business_name,
      legal_name,
      business_type,
      registration_number,
      website_url: ei.website || fd.website || '',
      short_bio
    },
    contact_address: {
      phone,
      business_email: ei.email || pd.email || '',
      emergency_contact,
      country_code: ei.country || '',
      state: ei.state || '',
      city: ei.city || '',
      postal_code: ei.zipCode || '',
      address_line1: ei.streetAddress || fd.address || '',
      location: ei.location || pd.location || ''
    },
    payment_settings: (() => {
      // If already nested, use it
      if (bank.method || bank.bank || bank.paypal) return bank;
      // If empty flat object, return empty
      if (!Object.keys(bank).length) return {};
      // Convert flat to nested bank format
      return {
        method: 'bank',
        verified: bank.verified || false,
        bank: {
          bankName: bank.bankName || '',
          holder: bank.accountHolderName || '',
          account: bank.accountNumber || '',
          swift: bank.swiftBic || '',
          country: bank.bankCountry || '',
          city: bank.bankCity || '',
          address: bank.bankAddress || '',
          iban: bank.iban || ''
        }
      };
    })(),
    social_links: fd.socialMediaLinks || {},
    documents: {
      logo_url: docs.logo || settings.logoPreview || settings.logoPublicId || '',
      business_license_url: docs.businessLicense || settings.businessLicensePreview || settings.businessLicensePublicId || '',
      liability_insurance_url: docs.liabilityInsurance || settings.liabilityInsurancePreview || settings.liabilityInsurancePublicId || ''
    },
    individual_info: {
      individual_name,
      personal_id_type: ''
    },
    contactNumbers: settings.contactNumbers || [],
    notifications: settings.notifications || {},
    reports: settings.reports || {},
    twoFactor: settings.twoFactor || {},
    verification_status: settings.verification_status || '',
    legal_data: settings.legal_data || settings.legalFormData || {},
    traveller_section: settings.traveller_section || settings.travellerSection || {}
  };
}

function denormalizeFromCanonical(canonical) {
  if (!canonical || !canonical.business_profile) return canonical;

  const bp = canonical.business_profile;
  const ca = canonical.contact_address;
  const ind = canonical.individual_info;
  const docs = canonical.documents || {};

  return {
    profileData: {
      publicName: bp.business_name,
      legalName: bp.legal_name,
      email: ca.business_email,
      location: ca.location,
      accountType: bp.business_type,
      streetAddress: ca.address_line1,
      zipCode: ca.postal_code,
      city: ca.city,
      state: ca.state,
      country: ca.country_code,
      contactValue: ca.phone
    },
    editableInfo: {
      publicName: bp.business_name,
      legalName: bp.legal_name,
      email: ca.business_email,
      location: ca.location,
      accountType: bp.business_type,
      streetAddress: ca.address_line1,
      zipCode: ca.postal_code,
      city: ca.city,
      state: ca.state,
      country: ca.country_code,
      contactValue: ca.phone,
      shortBio: bp.short_bio,
      emergencyContact: ca.emergency_contact,
      individualName: ind.individual_name,
      regNumber: bp.registration_number,
      website: bp.website_url
    },
    formData: {
      legalStatus: bp.business_type,
      legalName: bp.legal_name,
      contactInfo: ca.phone,
      emergencyContact: ca.emergency_contact,
      businessLicenseNumber: bp.registration_number,
      address: ca.address_line1,
      individualName: ind.individual_name,
      companyName: bp.business_name,
      description: bp.short_bio,
      website: bp.website_url,
      socialMediaLinks: canonical.social_links
    },
    bankingDetails: (() => {
      const ps = canonical.payment_settings || {};
      if (ps.method === 'bank' && ps.bank) {
        return {
          accountHolderName: ps.bank.holder || '',
          bankName: ps.bank.bankName || '',
          accountNumber: ps.bank.account || '',
          iban: ps.bank.iban || '',
          swiftBic: ps.bank.swift || '',
          bankAddress: ps.bank.address || '',
          bankCity: ps.bank.city || '',
          bankCountry: ps.bank.country || '',
          verified: ps.verified
        };
      }
      return ps;
    })(),
    contactNumbers: canonical.contactNumbers || [],
    notifications: canonical.notifications || {},
    reports: canonical.reports || {},
    twoFactor: canonical.twoFactor || {},
    documents: {
      logo: docs.logo_url,
      businessLicense: docs.business_license_url,
      liabilityInsurance: docs.liability_insurance_url
    },
    verification_status: canonical.verification_status,
    legal_data: canonical.legal_data || {},
    traveller_section: canonical.traveller_section || {}
  };
}

export const settingsService = {
  async getSettings(forceRefresh = false) {
    const now = Date.now();
    if (!forceRefresh && settingsCache && (now - cacheTimestamp) < CACHE_TTL) {
      return settingsCache;
    }
    try {
      const response = await apiService.get(`${BASE_PATH}`);
      const denormalized = { settings: denormalizeFromCanonical(response) };
      settingsCache = denormalized;
      cacheTimestamp = now;
      return denormalized;
    } catch (error) {
      if (settingsCache) return settingsCache;
      throw error;
    }
  },

  async saveSettings(settings) {
    let existing = settingsCache ? settingsCache.settings : null;
    if (!existing) {
      try {
        const fresh = await this.getSettings();
        existing = fresh.settings || {};
      } catch (e) {
        existing = {};
      }
    }
    
    // Safely merge incoming partial settings with the cached settings
    const mergedSettings = {
      ...existing,
      ...settings,
      formData: { ...(existing.formData || {}), ...(settings.formData || {}) },
      editableInfo: { ...(existing.editableInfo || {}), ...(settings.editableInfo || {}) },
      profileData: { ...(existing.profileData || {}), ...(settings.profileData || {}) },
      bankingDetails: { ...(existing.bankingDetails || {}), ...(settings.bankingDetails || settings.banking || {}) },
      documents: { ...(existing.documents || {}), ...(settings.documents || {}) },
      legal_data: settings.legal_data !== undefined ? settings.legal_data : (settings.legalFormData !== undefined ? settings.legalFormData : (existing.legal_data || {})),
      traveller_section: settings.traveller_section !== undefined ? settings.traveller_section : (settings.travellerSection !== undefined ? settings.travellerSection : (existing.traveller_section || {}))
    };

    // FIX: Synchronize overlapping fields so the newly submitted form overwrites the older cached form
    if (settings.formData) {
      // Company Profile was submitted, update editableInfo to match
      mergedSettings.editableInfo.publicName = settings.formData.companyName ?? mergedSettings.editableInfo.publicName;
      mergedSettings.editableInfo.legalName = settings.formData.legalName ?? mergedSettings.editableInfo.legalName;
      mergedSettings.editableInfo.contactValue = settings.formData.contactInfo ?? mergedSettings.editableInfo.contactValue;
      mergedSettings.editableInfo.streetAddress = settings.formData.address ?? mergedSettings.editableInfo.streetAddress;
      mergedSettings.editableInfo.accountType = settings.formData.legalStatus ?? mergedSettings.editableInfo.accountType;
      mergedSettings.editableInfo.regNumber = settings.formData.businessLicenseNumber ?? mergedSettings.editableInfo.regNumber;
      mergedSettings.editableInfo.website = settings.formData.website ?? mergedSettings.editableInfo.website;
      mergedSettings.editableInfo.shortBio = settings.formData.description ?? mergedSettings.editableInfo.shortBio;
      mergedSettings.editableInfo.emergencyContact = settings.formData.emergencyContact ?? mergedSettings.editableInfo.emergencyContact;
      mergedSettings.editableInfo.individualName = settings.formData.individualName ?? mergedSettings.editableInfo.individualName;
    } else if (settings.editableInfo) {
      // Account Settings was submitted, update formData to match
      mergedSettings.formData.companyName = settings.editableInfo.publicName ?? mergedSettings.formData.companyName;
      mergedSettings.formData.legalName = settings.editableInfo.legalName ?? mergedSettings.formData.legalName;
      mergedSettings.formData.contactInfo = settings.editableInfo.contactValue ?? mergedSettings.formData.contactInfo;
      mergedSettings.formData.address = settings.editableInfo.streetAddress ?? mergedSettings.formData.address;
      mergedSettings.formData.legalStatus = settings.editableInfo.accountType ?? mergedSettings.formData.legalStatus;
      mergedSettings.formData.businessLicenseNumber = settings.editableInfo.regNumber ?? mergedSettings.formData.businessLicenseNumber;
      mergedSettings.formData.website = settings.editableInfo.website ?? mergedSettings.formData.website;
      mergedSettings.formData.description = settings.editableInfo.shortBio ?? mergedSettings.formData.description;
      mergedSettings.formData.emergencyContact = settings.editableInfo.emergencyContact ?? mergedSettings.formData.emergencyContact;
      mergedSettings.formData.individualName = settings.editableInfo.individualName ?? mergedSettings.formData.individualName;
    }

    const payload = normalizeToCanonical(mergedSettings);
    
    console.group("[SUPPLIER FORM SUBMIT]");
    console.log("PAGE:", "Settings Service (Handles all 3 forms)");
    console.log("INCOMING FORM STATE:", settings);
    console.log("MERGED STATE:", mergedSettings);
    console.log("CANONICAL PAYLOAD:", payload);
    console.log("ENDPOINT:", `${BASE_PATH}/profile`);
    console.log("METHOD:", "PUT");
    console.groupEnd();

    try {
      const response = await apiService.put(`${BASE_PATH}/profile`, payload);
      
      console.group("[SUPPLIER FORM RESPONSE]");
      console.log("PAGE:", "Settings Service");
      console.log("STATUS:", response?.status || "Unknown");
      console.log("RESPONSE:", response?.data || response);
      console.groupEnd();

      this.clearCache();
      return response;
    } catch (error) {
      console.group("[SUPPLIER FORM ERROR]");
      console.log("PAGE:", "Settings Service");
      console.log("STATUS:", error?.response?.status);
      console.log("REQUEST:", error?.config);
      console.log("BACKEND RESPONSE:", error?.response?.data);
      console.groupEnd();
      throw error;
    }
  },

  async updateProfile(profileSettings) {
    return this.saveSettings(profileSettings);
  },

  async updateAccount(accountSettings) {
    return this.saveSettings(accountSettings);
  },

  async updateBanking(bankingSettings) {
    return this.saveSettings(bankingSettings.bankingDetails ? bankingSettings : { bankingDetails: bankingSettings });
  },

  async updateNotifications(notificationSettings) {
    return this.saveSettings({ notifications: notificationSettings });
  },

  clearCache() {
    settingsCache = null;
    cacheTimestamp = 0;
  },
};